import type { DutyRequestStatus, DutyRequestWithRelations, DutyRequirements } from "@dutyon/apis";
import {
  Box,
  Button,
  Collapse,
  type DutyData,
  DutyEditor,
  DutyRequirementsEditor,
  Paper,
  SafeHtml,
  Stack,
  Stepper,
  Text,
  useDisclosure,
} from "@dutyon/ui-kit";
import { formatDutyRequestDateKey } from "../../services/dutyRequestDate";

interface DutyRequestInfoProps {
  request: DutyRequestWithRelations;
}

const STATUS_STEPS: { status: DutyRequestStatus; label: string; description: string }[] = [
  { status: "PENDING", label: "요청 접수", description: "근무표 작성 대기 중" },
  { status: "APPROVED", label: "최종 확정", description: "근무표가 확정되었습니다" },
];

function getActiveStep(status: DutyRequestStatus): number {
  if (status === "APPROVED") return 1;
  return 0;
}

export function DutyRequestInfo({ request }: DutyRequestInfoProps) {
  const activeStep = getActiveStep(request.status as DutyRequestStatus);
  const [opened, { toggle }] = useDisclosure(false);

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="lg">
        {/* 진행 상태 스테퍼 */}
        <Stepper
          active={activeStep}
          size="sm"
          color={request.status === "APPROVED" ? "green" : "blue"}
        >
          {STATUS_STEPS.map((step) => (
            <Stepper.Step key={step.status} label={step.label} description={step.description} />
          ))}
        </Stepper>

        {/* 기본 정보 */}
        <Stack gap="xs">
          <Text>
            <strong>요청 기간:</strong> {formatDutyRequestDateKey(request.startDate)} ~{" "}
            {formatDutyRequestDateKey(request.endDate)}
          </Text>
          <Text>
            <strong>팀:</strong> {request.team.name}
          </Text>
          {request.team.company && (
            <Text>
              <strong>회사:</strong> {request.team.company.name}
            </Text>
          )}
        </Stack>

        <Box mt="md">
          <Button variant="light" onClick={toggle} fullWidth>
            {opened ? "기존 근무 요청 내역 접기" : "기존 근무 요청 내역 보기"}
          </Button>
          <Collapse in={opened}>
            <Stack gap="md" mt="md">
              {/* 일자별 필요 인원수 에디터 (disabled) */}
              <Box>
                <Text fw={600} size="sm" mb={6}>
                  일자별 필요 인원 설정
                </Text>
                <DutyRequirementsEditor
                  nurses={request.targetNurses || []}
                  startDate={new Date(`${formatDutyRequestDateKey(request.startDate)}T00:00:00`)}
                  endDate={new Date(`${formatDutyRequestDateKey(request.endDate)}T00:00:00`)}
                  workShifts={request.workShifts || []}
                  value={request.requirements as unknown as Record<string, Record<string, number>>}
                  disabled={true}
                />
              </Box>

              {/* 근무표 초안 에디터 (readOnly) */}
              <Box>
                <Text fw={600} size="sm" mb={6}>
                  근무표 초안
                </Text>
                <Box
                  p="md"
                  style={{
                    border: "1px solid var(--mantine-color-gray-3)",
                    borderRadius: "var(--mantine-radius-md)",
                    backgroundColor: "var(--mantine-color-gray-0)",
                  }}
                >
                  <DutyEditor
                    nurses={request.targetNurses || []}
                    workShifts={request.workShifts || []}
                    wanteds={request.wanteds || []}
                    requirements={request.requirements as unknown as DutyRequirements}
                    startDate={new Date(`${formatDutyRequestDateKey(request.startDate)}T00:00:00`)}
                    endDate={new Date(`${formatDutyRequestDateKey(request.endDate)}T00:00:00`)}
                    value={(request.scheduleData as unknown as DutyData) || {}}
                    readOnly={true}
                  />
                </Box>
              </Box>
            </Stack>
          </Collapse>
        </Box>

        {/* 메모 (HTML) */}
        {request.memo && (
          <Box>
            <Text fw={600} size="sm" mb={8}>
              메모
            </Text>
            <Box
              p="md"
              style={{
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: "var(--mantine-radius-md)",
                backgroundColor: "var(--mantine-color-gray-0)",
                minHeight: 120,
                lineHeight: 1.7,
              }}
            >
              <SafeHtml
                html={request.memo}
                style={{
                  fontSize: "var(--mantine-font-size-sm)",
                  color: "var(--mantine-color-dark-7)",
                }}
              />
            </Box>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
